import {
  format,
  formatDistanceToNow,
  isSameDay,
  isSameYear,
  isThisWeek,
  isToday,
  isTomorrow,
  isValid,
  isYesterday,
  parse,
  startOfYesterday,
  startOfWeek,
  startOfMonth,
  startOfYear,
  subYears,
  subMonths,
  subWeeks,
  subDays,
  addDays,
  startOfToday,
  isAfter,
} from 'date-fns'
import { nl, enGB } from 'date-fns/locale'
import { TFunction } from 'i18next'

export const AllWeekdays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
export type Weekday = (typeof AllWeekdays)[number]

export function localeForLanguage(language?: string) {
  switch (language) {
    case 'nl':
      return nl
    default:
      return enGB
  }
}

export function dateFormatter(date: number | Date | string, dateFormat: string, language?: string) {
  if (!date) return
  const locale = localeForLanguage(language)
  const value = typeof date === 'string' ? new Date(date) : date
  return format(value, dateFormat, { locale })
}

export function localizedDate(date: number | Date | string, language?: string) {
  if (!date) return
  return dateFormatter(date, 'd LLL, yyyy', language)
}

export function shortDate(date: number | Date | string, language?: string) {
  if (isSameYear(new Date(), date)) return dateFormatter(date, 'd LLL', language)
  else return dateFormatter(date, 'd LLL, yyyy', language)
}

export function shortLocalizedDate(date: number | Date | string, language?: string, t?: TFunction) {
  if (!date) return
  const value = typeof date === 'string' ? new Date(date) : date
  if (t && isToday(value)) return t('Today')
  else if (t && isTomorrow(value)) return t('Tomorrow')
  else if (t && isYesterday(value)) return t('Yesterday')
  else if (isThisWeek(value)) return dateFormatter(date, 'eeee', language)
  const localizedDate = dateFormatter(date, 'd LLL, yyyy', language)
  const year = dateFormatter(new Date(), 'yyyy', language)
  return localizedDate?.replace(new RegExp(`,? ${year}`), '')
}

export function localizedTime(date: number | Date | string, language?: string) {
  if (!date) return
  return dateFormatter(date, 'H:mm:ss', language)
}

export function shortLocalizedTime(date: number | Date | string, language?: string) {
  if (!date) return
  const localizedTime = dateFormatter(date, 'H:mm', language)
  return localizedTime
}

export function shortLocalizedDateAndTime(date: number | Date | string, language?: string, t?: TFunction) {
  if (!date) return
  const value = typeof date === 'string' ? new Date(date) : date
  if (t && isToday(value)) return `${t('Today', { lng: language })} ${dateFormatter(date, 'H:mm', language)}`
  else if (t && isTomorrow(value)) return `${t('Tomorrow', { lng: language })} ${dateFormatter(date, 'H:mm', language)}`
  else if (t && isYesterday(value))
    return `${t('Yesterday', { lng: language })} ${dateFormatter(date, 'H:mm', language)}`
  else if (isThisWeek(value)) return dateFormatter(date, 'eeee H:mm', language)
  else return `${shortLocalizedDate(date, language)} ${dateFormatter(date, 'H:mm', language)}`
}

export function dateToNow(date: number | Date | string, language?: string) {
  if (!date) return
  const locale = localeForLanguage(language)
  const value = typeof date === 'string' ? new Date(date) : date
  return formatDistanceToNow(value, { locale })
}

export function parseDate(text?: string, language?: string) {
  if (!text) return undefined
  const locale = localeForLanguage(language)

  const config = [
    {
      format: 'P',
    },
    {
      format: 'PP',
    },
    {
      format: 'PPP',
    },
    {
      format: 'PPPP',
    },
    {
      format: 'P',
      transform: (text: string) => text.replaceAll('/', '-'),
    },
    {
      format: 'P',
      transform: (text: string) => text.replaceAll('-', '/'),
    },
  ]
  let date: Date | undefined
  config.some(({ format, transform }) => {
    date = parse(transform ? transform(text) : text, format, new Date(), { locale })
    return isValid(date)
  })
  return date
}

export const displayTitlesForCalendarValue = (value: Date[], language?: string) => {
  if (value?.length > 1 && isSameDay(value?.[0], value?.[1])) {
    return [shortLocalizedDate(value[0], language)]
  } else if (value?.length > 1 && !isSameYear(value?.[0], value?.[1])) {
    return value?.map((date) => localizedDate(date, language))
  } else {
    return value?.map((date) => shortLocalizedDate(date, language))
  }
}

export const datePresets = (t?: TFunction): { value: Date[]; title: string; disabled?: boolean }[] => {
  const today = startOfToday()
  const yesterday = startOfYesterday()
  const weekStart = startOfWeek(today, { weekStartsOn: 1 })
  const previousWeekStart = subWeeks(weekStart, 1)
  const previousWeekEnd = subDays(weekStart, 1)
  const monthStart = startOfMonth(today)
  const previousMonthStart = subMonths(monthStart, 1)
  const previousMonthEnd = subDays(monthStart, 1)
  const yearStart = startOfYear(today)
  const previousYearStart = subYears(yearStart, 1)
  const previousYearEnd = subDays(yearStart, 1)

  return [
    // FIXME: Temperarily disable insights for today
    // {
    //   title: t?.('Today'),
    //   value: [today, today],
    // },
    {
      title: t?.('Yesterday') || 'Yesterday',
      value: [yesterday, yesterday],
    },
    {
      title: t?.('This week') || 'This week',
      // FIXME: Temperarily disable insights for today
      // value: [weekStart, today],
      value: [weekStart, yesterday],
      disabled: isAfter(weekStart, yesterday),
    },
    {
      title: t?.('Last week') || 'Last week',
      value: [previousWeekStart, previousWeekEnd],
    },
    {
      title: t?.('This month') || 'This month',
      // FIXME: Temperarily disable insights for today
      // value: [monthStart, today],
      value: [monthStart, yesterday],
      disabled: isAfter(monthStart, yesterday),
    },
    {
      title: t?.('Last month') || 'Last month',
      value: [previousMonthStart, previousMonthEnd],
    },
    {
      title: t?.('This year') || 'This year',
      // FIXME: Temperarily disable insights for today
      // value: [yearStart, today],
      value: [yearStart, yesterday],
      disabled: isAfter(yearStart, yesterday),
    },
    {
      title: t?.('Last year') || 'Last year',
      value: [previousYearStart, previousYearEnd],
    },
  ]
}

export const defaultDatePreset = (t?: TFunction) => {
  const presets = datePresets(t)
  return presets[1].disabled ? presets[2] : presets[1]
}

export const sanitizeCalendarValue = (value: Date[]): [Date, Date] | undefined => {
  const start = value?.[0]
  const end = value?.[1]
  if (!start) return undefined
  if (!end) return [start, start]
  return [start, end]
}

export const datePresetForValue = (value: Date[], t?: TFunction) => {
  const dates = sanitizeCalendarValue(value)
  const start = dates?.[0]
  const end = dates?.[1]
  return datePresets(t).find((preset) => {
    const [presetStart, presetEnd] = sanitizeCalendarValue(preset.value)!
    if (!start || !presetStart || !end || !presetEnd) return false
    return isSameDay(start, presetStart) && isSameDay(end, presetEnd)
  })
}

export const calendarValueToInsightsFromTo = (value?: Date[], weekdays?: Weekday[]) => {
  if (!value) return
  const [start, end] = sanitizeCalendarValue(value) || []
  const calendarValue = { from: start, to: end }
  if (weekdays?.length) {
    while (weekdays.indexOf(AllWeekdays[calendarValue.from?.getDay() || 0]) < 0) {
      calendarValue.from = addDays(calendarValue.from || new Date(), 1)
    }
    while (weekdays.indexOf(AllWeekdays[calendarValue.to?.getDay() || 0]) < 0) {
      calendarValue.to = subDays(calendarValue.to || new Date(), 1)
    }
    if (calendarValue.from && calendarValue.to && calendarValue.from > calendarValue.to) return
  }
  return calendarValue
}

export const stringifyCalendarValue = (value: Date[]) => {
  return value?.map((date) => format(date, 'yyyy-MM-dd')).join(',')
}

export const parseCalendarValue = (value?: string) => {
  return value?.split(',')?.map((text) => new Date(text)) as Date[]
}

export const displayTitleForCalendarValue = (value: Date[], language?: string, t?: TFunction) => {
  const preset = datePresetForValue(value, t)
  if (preset) return preset.title?.toLocaleLowerCase()
  else {
    const displayPeriodTitles = displayTitlesForCalendarValue(value, language)
    return displayPeriodTitles.length === 1
      ? t?.('on {{day}}', { day: displayPeriodTitles[0] })
      : displayPeriodTitles.length === 2
        ? t?.('between {{start}} and {{end}}', { start: displayPeriodTitles[0], end: displayPeriodTitles[1] })
        : ''
  }
}

export const getWeekdaysInRange = (start?: Date, end?: Date): Weekday[] => {
  if (!start || !end) return [...AllWeekdays]
  const result = new Set<Weekday>()
  let current = start
  while (current <= end && result.size < 7) {
    result.add(AllWeekdays[current.getDay()])
    current = addDays(current, 1)
  }
  return [...result]
}

export function titleForWeekday(weekday: Weekday, t?: TFunction) {
  switch (weekday) {
    case 'mon':
      return t?.('Monday') || 'Monday'
    case 'tue':
      return t?.('Tuesday') || 'Tuesday'
    case 'wed':
      return t?.('Wednesday') || 'Wednesday'
    case 'thu':
      return t?.('Thursday') || 'Thursday'
    case 'fri':
      return t?.('Friday') || 'Friday'
    case 'sat':
      return t?.('Saturday') || 'Saturday'
    case 'sun':
      return t?.('Sunday') || 'Sunday'
    default:
      return weekday
  }
}
